import { Controller } from "stimulus";

const csrfToken = (): string => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char] || char);

const COLLAPSED_KEY = "testing-tools:rules:collapsed";
let collapsedInMemory: string[] = [];

const readCollapsed = (): string[] => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(COLLAPSED_KEY) || "null");
    return Array.isArray(stored) ? stored : collapsedInMemory;
  } catch {
    return collapsedInMemory;
  }
};

const writeCollapsed = (groups: string[]): void => {
  collapsedInMemory = groups;
  try {
    window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(groups));
  } catch {
    return;
  }
};

export default class extends Controller {
  static targets = ["resetConfirm"];
  static values = { indexUrl: String, mode: String, scope: String, mailType: String };

  declare readonly resetConfirmTarget: HTMLElement;
  declare indexUrlValue: string;
  declare modeValue: string;
  declare scopeValue: string;
  declare mailTypeValue: string;

  connect(): void {
    this.applyCollapsed();
  }

  setMode(event: Event): void {
    this.reload({ delivery_mode: (event.currentTarget as HTMLElement).dataset.value || "mailcatcher", bot_id: this.scopeValue, mail_type: this.mailTypeValue });
  }

  setScope(event: Event): void {
    this.reload({ delivery_mode: this.modeValue, bot_id: (event as CustomEvent<{ id: string }>).detail.id, mail_type: this.mailTypeValue });
  }

  setMailType(event: Event): void {
    const mailType = (event.currentTarget as HTMLElement).dataset.value || "all";
    const url = new URL(window.location.href);
    url.searchParams.set("mail_type", mailType);
    window.history.replaceState(window.history.state, "", url.toString());
    this.reload({ delivery_mode: this.modeValue, bot_id: this.scopeValue, mail_type: mailType });
  }

  toggleGroup(event: Event): void {
    const group = (event.currentTarget as HTMLElement).dataset.group || "";
    const collapsed = readCollapsed().filter((name) => name !== group);
    if ((event.currentTarget as HTMLElement).getAttribute("aria-expanded") === "true") collapsed.push(group);
    writeCollapsed(collapsed);
    this.applyCollapsed();
  }

  flip(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    this.closePreviews(input.dataset.key);
    const extra: Record<string, string> = { enabled: String(input.checked) };
    if (this.mailTypeValue !== "all") extra.mail_type = this.mailTypeValue;
    this.loadPreview(input.dataset.key || "", extra);
  }

  previewCampaign(event: Event): void {
    const body = this.previewBody(event);
    if (body) this.loadPreview(body.dataset.key || "", { ...this.previewState(body), preview_bot_id: (event as CustomEvent<{ id: string }>).detail.id });
  }

  previewMailType(event: Event): void {
    const body = this.previewBody(event);
    if (body) this.loadPreview(body.dataset.key || "", { ...this.previewState(body), mail_type: (event.currentTarget as HTMLElement).dataset.mailType || "initial" });
  }

  previewPage(event: Event): void {
    const body = this.previewBody(event);
    if (body) this.loadPreview(body.dataset.key || "", { ...this.previewState(body), page: (event.currentTarget as HTMLElement).dataset.page || "1" });
  }

  async apply(event: Event): Promise<void> {
    const body = this.previewBody(event);
    if (!body) return;
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    const form = new URLSearchParams({ delivery_mode: this.modeValue, bot_id: this.scopeValue, mail_type: this.mailTypeValue, enabled: body.dataset.enabled || "true" });
    const response = await this.write(`${this.indexUrlValue}/${body.dataset.key}`, "PATCH", form);
    if (response.ok) this.replace(await response.text());
    else button.disabled = false;
  }

  cancel(event: Event): void {
    const body = this.previewBody(event);
    if (!body) return;
    this.closePreviews();
    const input = this.element.querySelector<HTMLInputElement>(`input[data-key="${body.dataset.key}"]`);
    if (input) input.checked = body.dataset.enabled !== "true";
  }

  askReset(): void {
    this.resetConfirmTarget.hidden = false;
  }

  cancelReset(): void {
    this.resetConfirmTarget.hidden = true;
  }

  async reset(): Promise<void> {
    const form = new URLSearchParams({ delivery_mode: this.modeValue, bot_id: this.scopeValue, mail_type: this.mailTypeValue });
    const response = await this.write(`${this.indexUrlValue}/reset`, "DELETE", form);
    if (response.ok) this.replace(await response.text());
  }

  private async loadPreview(key: string, extra: Record<string, string>): Promise<void> {
    const slot = this.element.querySelector<HTMLElement>(`[data-preview-for="${key}"]`);
    if (!slot) return;
    const url = new URL(`${this.indexUrlValue}/${key}/preview`, window.location.origin);
    Object.entries({ delivery_mode: this.modeValue, bot_id: this.scopeValue, ...extra }).forEach(([name, value]) => url.searchParams.set(name, value));
    slot.hidden = false;
    slot.innerHTML = '<p class="muted">Finding the candidates this affects…</p>';
    const response = await fetch(url.toString(), { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" });
    slot.innerHTML = response.ok ? await response.text() : `<p class="muted">${escapeHtml((await response.json().catch(() => ({ error: "Couldn't load the preview" }))).error)}</p>`;
  }

  private applyCollapsed(): void {
    const collapsed = readCollapsed();
    this.element.querySelectorAll<HTMLElement>("[data-group-body]").forEach((body) => {
      body.hidden = collapsed.includes(body.dataset.groupBody || "");
    });
    this.element.querySelectorAll<HTMLElement>(".tt-group-toggle").forEach((button) => {
      button.setAttribute("aria-expanded", String(!collapsed.includes(button.dataset.group || "")));
    });
  }

  private previewState(body: HTMLElement): Record<string, string> {
    return { enabled: body.dataset.enabled || "true", mail_type: body.dataset.mailType || "initial", preview_bot_id: body.dataset.previewBot || "" };
  }

  private previewBody(event: Event): HTMLElement | null {
    return (event.currentTarget as HTMLElement).closest<HTMLElement>(".tt-preview-body");
  }

  private closePreviews(except?: string): void {
    this.element.querySelectorAll<HTMLElement>("[data-preview-for]").forEach((slot) => {
      if (slot.dataset.previewFor === except) return;
      if (!slot.hidden) {
        const body = slot.querySelector<HTMLElement>(".tt-preview-body");
        const input = this.element.querySelector<HTMLInputElement>(`input[data-key="${slot.dataset.previewFor}"]`);
        if (body && input) input.checked = body.dataset.enabled !== "true";
      }
      slot.hidden = true;
      slot.innerHTML = "";
    });
  }

  private async reload(params: Record<string, string>): Promise<void> {
    const url = new URL(this.indexUrlValue, window.location.origin);
    Object.entries(params).forEach(([name, value]) => url.searchParams.set(name, value));
    const response = await fetch(url.toString(), { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" });
    if (response.ok) this.replace(await response.text());
  }

  private write(url: string, method: string, body: URLSearchParams): Promise<Response> {
    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "text/html", "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
  }

  private replace(html: string): void {
    this.element.outerHTML = html;
  }
}
